// frontend/src/pages/Profile/components/IntegrationsTab.tsx
import React, { useState, useEffect } from 'react';
import { 
  Stack, 
  Title, 
  Text, 
  Card, 
  Group, 
  Button, 
  ThemeIcon, 
  Center,
  Loader,
  Modal,
  useMantineTheme
} from '@mantine/core';
import {
  IconApi,
  IconCheck,
  IconSettings,
  IconAlertCircle,
  IconBrandGoogleDrive,
  IconHome2
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mantine/hooks';
import { integrationsApi, GoogleDriveStatus } from '../../../api/integrations.api';
import Beds24Integration from './Beds24Integration';
import HostexIntegration from './HostexIntegration';
import GoogleDriveIntegration from './GoogleDriveIntegration';

const IntegrationsTab: React.FC = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [gdriveStatus, setGdriveStatus] = useState<GoogleDriveStatus | null>(null);

  useEffect(() => {
    loadIntegrations();
    loadGoogleDriveStatus();

    // Check URL params for tab selection (from OAuth callback redirect)
    const params = new URLSearchParams(window.location.search);
    if (params.get('gdrive')) {
      setSelectedIntegration('google-drive');
    }
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await integrationsApi.getIntegrations();
      
      if (response.data.success) {
        setIntegrations(response.data.data);
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleDriveStatus = async () => {
    try {
      const response = await integrationsApi.googleDriveStatus();
      if (response.data.success) {
        setGdriveStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error loading Google Drive status:', error);
    }
  };

  const handleOpenIntegration = (type: string) => {
    setSelectedIntegration(type);
  };

  const handleCloseIntegration = () => {
    setSelectedIntegration(null);
    loadIntegrations();
    loadGoogleDriveStatus();
  };

  const beds24Integration = integrations.find(i => i.integration_type === 'beds24');
  const isBeds24Configured = beds24Integration && beds24Integration.is_verified;
  const hostexIntegration = integrations.find(i => i.integration_type === 'hostex');
  const isHostexConfigured = hostexIntegration && hostexIntegration.is_verified;
  const isGdriveConnected = gdriveStatus?.connected;

  if (loading) {
    return (
      <Center py={60}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            {t('common.loading')}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="xl">
      <div>
        <Title order={4}>{t('integrations.title')}</Title>
        <Text size="sm" c="dimmed" mt="xs">
          {t('integrations.description')}
        </Text>
      </div>

      <Stack gap="md">
        <Card
          shadow="sm"
          p={isMobile ? 'md' : 'xl'}
          radius="md"
          withBorder
          style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.md;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = theme.shadows.sm;
          }}
        >
          <Group justify="space-between" wrap={isMobile ? 'wrap' : 'nowrap'} gap="lg">
            <Group gap="lg" wrap="nowrap" style={{ flex: 1 }}>
              <ThemeIcon
                size={isMobile ? 48 : 56}
                radius="md"
                variant="light"
                color="blue"
              >
                <IconApi size={isMobile ? 24 : 28} />
              </ThemeIcon>
              
              <Stack gap={4} style={{ flex: 1 }}>
                <Title order={5}>Beds24</Title>
                <Group gap="xs">
                  {isBeds24Configured ? (
                    <>
                      <ThemeIcon size="xs" color="green" variant="light" radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {t('integrations.beds24.configured')}
                      </Text>
                    </>
                  ) : (
                    <>
                      <ThemeIcon size="xs" color="orange" variant="light" radius="xl">
                        <IconAlertCircle size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {t('integrations.beds24.notConfigured')}
                      </Text>
                    </>
                  )}
                </Group>
              </Stack>
            </Group>

            <Button
              variant={isBeds24Configured ? 'light' : 'gradient'}
              gradient={isBeds24Configured ? undefined : { from: 'blue', to: 'cyan' }}
              leftSection={<IconSettings size={18} />}
              onClick={() => handleOpenIntegration('beds24')}
              size={isMobile ? 'sm' : 'md'}
              fullWidth={isMobile}
            >
              {isBeds24Configured ? t('integrations.manage') : t('integrations.setup')}
            </Button>
          </Group>
        </Card>

        {/* Hostex Card */}
        <Card
          shadow="sm"
          p={isMobile ? 'md' : 'xl'}
          radius="md"
          withBorder
          style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.md;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = theme.shadows.sm;
          }}
        >
          <Group justify="space-between" wrap={isMobile ? 'wrap' : 'nowrap'} gap="lg">
            <Group gap="lg" wrap="nowrap" style={{ flex: 1 }}>
              <ThemeIcon
                size={isMobile ? 48 : 56}
                radius="md"
                variant="light"
                color="grape"
              >
                <IconHome2 size={isMobile ? 24 : 28} />
              </ThemeIcon>

              <Stack gap={4} style={{ flex: 1 }}>
                <Title order={5}>Hostex</Title>
                <Group gap="xs">
                  {isHostexConfigured ? (
                    <>
                      <ThemeIcon size="xs" color="green" variant="light" radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {t('integrations.hostex.configured')}
                      </Text>
                    </>
                  ) : (
                    <>
                      <ThemeIcon size="xs" color="orange" variant="light" radius="xl">
                        <IconAlertCircle size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {t('integrations.hostex.notConfigured')}
                      </Text>
                    </>
                  )}
                </Group>
              </Stack>
            </Group>

            <Button
              variant={isHostexConfigured ? 'light' : 'gradient'}
              gradient={isHostexConfigured ? undefined : { from: 'grape', to: 'pink' }}
              color={isHostexConfigured ? 'grape' : undefined}
              leftSection={<IconSettings size={18} />}
              onClick={() => handleOpenIntegration('hostex')}
              size={isMobile ? 'sm' : 'md'}
              fullWidth={isMobile}
            >
              {isHostexConfigured ? t('integrations.manage') : t('integrations.setup')}
            </Button>
          </Group>
        </Card>

        {/* Google Drive Card */}
        <Card
          shadow="sm"
          p={isMobile ? 'md' : 'xl'}
          radius="md"
          withBorder
          style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.md;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = theme.shadows.sm;
          }}
        >
          <Group justify="space-between" wrap={isMobile ? 'wrap' : 'nowrap'} gap="lg">
            <Group gap="lg" wrap="nowrap" style={{ flex: 1 }}>
              <ThemeIcon
                size={isMobile ? 48 : 56}
                radius="md"
                variant="light"
                color="green"
              >
                <IconBrandGoogleDrive size={isMobile ? 24 : 28} />
              </ThemeIcon>

              <Stack gap={4} style={{ flex: 1 }}>
                <Title order={5}>Google Drive</Title>
                <Group gap="xs">
                  {isGdriveConnected ? (
                    <>
                      <ThemeIcon size="xs" color="green" variant="light" radius="xl">
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {gdriveStatus?.email || t('integrations.googleDrive.connected')}
                      </Text>
                    </>
                  ) : (
                    <>
                      <ThemeIcon size="xs" color="orange" variant="light" radius="xl">
                        <IconAlertCircle size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {t('integrations.googleDrive.notConnected')}
                      </Text>
                    </>
                  )}
                </Group>
              </Stack>
            </Group>

            <Button
              variant={isGdriveConnected ? 'light' : 'gradient'}
              gradient={isGdriveConnected ? undefined : { from: 'green', to: 'teal' }}
              color={isGdriveConnected ? 'green' : undefined}
              leftSection={<IconSettings size={18} />}
              onClick={() => handleOpenIntegration('google-drive')}
              size={isMobile ? 'sm' : 'md'}
              fullWidth={isMobile}
            >
              {isGdriveConnected ? t('integrations.manage') : t('integrations.setup')}
            </Button>
          </Group>
        </Card>

        {integrations.length === 0 && (
          <Card shadow="sm" p="xl" radius="md" withBorder>
            <Center>
              <Stack align="center" gap="md">
                <ThemeIcon size={80} radius="xl" variant="light" color="gray">
                  <IconApi size={40} />
                </ThemeIcon>
                <Text size="lg" fw={500} c="dimmed">
                  {t('integrations.noIntegrations')}
                </Text>
                <Text size="sm" c="dimmed" ta="center">
                  {t('integrations.noIntegrationsDescription')}
                </Text>
              </Stack>
            </Center>
          </Card>
        )}
      </Stack>

      <Modal
        opened={selectedIntegration === 'beds24'}
        onClose={handleCloseIntegration}
        title={
          <Group gap="sm">
            <ThemeIcon size="lg" radius="md" variant="light" color="blue">
              <IconApi size={20} />
            </ThemeIcon>
            <Text fw={600}>Beds24</Text>
          </Group>
        }
        size={isMobile ? 'full' : 'xl'}
        centered
        padding={isMobile ? 'md' : 'xl'}
      >
        <Beds24Integration onClose={handleCloseIntegration} />
      </Modal>

      <Modal
        opened={selectedIntegration === 'hostex'}
        onClose={handleCloseIntegration}
        title={
          <Group gap="sm">
            <ThemeIcon size="lg" radius="md" variant="light" color="grape">
              <IconHome2 size={20} />
            </ThemeIcon>
            <Text fw={600}>Hostex</Text>
          </Group>
        }
        size={isMobile ? 'full' : 'xl'}
        centered
        padding={isMobile ? 'md' : 'xl'}
      >
        <HostexIntegration onClose={handleCloseIntegration} />
      </Modal>

      <Modal
        opened={selectedIntegration === 'google-drive'}
        onClose={handleCloseIntegration}
        title={
          <Group gap="sm">
            <ThemeIcon size="lg" radius="md" variant="light" color="green">
              <IconBrandGoogleDrive size={20} />
            </ThemeIcon>
            <Text fw={600}>Google Drive</Text>
          </Group>
        }
        size={isMobile ? 'full' : 'lg'}
        centered
        padding={isMobile ? 'md' : 'xl'}
      >
        <GoogleDriveIntegration onClose={handleCloseIntegration} />
      </Modal>
    </Stack>
  );
};

export default IntegrationsTab;